<template>
    <froala
        v-if="loaded"
        :tag="'textarea'"
        :config="froalaConfig"
        ref="froala"
        v-model="content"
    ></froala>
</template>

<script>
import moment from 'moment';
import emailApi from '~/api/email';
import DateFormatEnum from "~/enums/DateFormatEnum";
import SendStateEnum from "~/pages/email/enums/SendStateEnum";
import { mapGetters } from "vuex";
import DOMPurify from "dompurify";

export default {
  name: "ContentComposerComponent",
  props:{
    signature:{
      type: String,
      required: true
    },
    footer:{
      type: Object,
      required: true
    },
    templateContents: {
      type: String,
      required: true
    },
    value:{
      type: String,
      required: true
    }
  },
  data(){
    return {
      froalaConfig: {
        key: window.state.froala_key,
        pastePlain: true,
        imagePaste: false,
        htmlRemoveTags: ['script'],
        pasteDeniedAttrs: ['id'],
        iframeStyleFiles: ["/assets/css/bootstrap.css", "/assets/css/noneditable.css"],
        pluginsEnabled: [
          'align', 'colors', 'fontFamily', 'fontSize', 'lineHeight', 'link', 'lists', 'quote', 'table', 'url', 'wordPaste',
        ],
        iframe: true,
        iframeStyle: `
          .signature {
            border: dashed 2px #dadada;
            padding: .5rem;
            border-radius: .5rem;
            padding-top: 2.5rem;
            position: relative;
            overflow:  hidden;
            user-select: none;
          }
          .signature.hidden {
            display: none !important;
          }
          .signature:before {
            content: 'E-mailhandtekening';
            position: absolute;
            top: 0;
            left: 0;
            width:  100%;
            background: #ebebeb;
            font-size: 1.2rem;
            padding: .2rem .5rem;
            color: #5d5d5d;
            border-bottom: solid 1px #dadada;
            font-weight: 500;
          }
          .footer{
            max-width: 600px;
          }
        `
      },
      signatureId: this.customID(),
      footerId: this.customID(),
      originalEmail: '',
      loaded: false
    }
  },
  async mounted(){
    if (this.firstSelectedEmail) await this.loadEmail();
    this.composeContent();
  },
  watch: {
    firstSelectedEmail(){
      this.loadEmail();
    },
    templateContents(){
      this.composeContent();
    },
    signature(){
      this.content = this.updateSignature(this.content);
    },
    footer(){
      this.content = this.updateFooter(this.content);
    },
  },
  methods: {
    async loadEmail(){
      this.originalEmail = (await emailApi.getBody(this.firstSelectedEmail.id)).body;
    },
    composeContent(){
      let newContent = `
        ${this.templateContents}<br/> <br/> <br/>
        <div id="${this.signatureId}" class="signature"></div>
        <br/>
        <img id="${this.footerId}" class="footer" src="${this.footerData}" alt="">
        <br/>
        <br/>
      `;

      if (this.firstSelectedEmail && (this.sendState === SendStateEnum.REPLY || this.sendState === SendStateEnum.FORWARD)){
        newContent = newContent + `
            -- Originele bericht --
            <br/>
            <b>Onderwerp:</b> ${this.firstSelectedEmail.subject}<br/>
            <b>Afzender:</b> ${this.firstSelectedEmail.from}<br/>
            <b>Ontvanger:</b> ${this.firstSelectedEmail.to}<br/>
            <b>Verzonden:</b> ${moment(this.firstSelectedEmail.date).format(DateFormatEnum.DISPLAY_DATETIME_WITH_DAY)}<br/>
            <br/>
            ${this.originalEmail}
        `;
      }

      this.content = this.updateSignature(newContent);
      this.loaded = true;
    },
    updateSignature(content){
      const buffer = document.createElement('div');
      buffer.innerHTML = DOMPurify.sanitize(content);
      const signatureDiv = buffer.querySelector(`#${this.signatureId}`);
      signatureDiv.innerHTML = DOMPurify.sanitize(this.signature);
      signatureDiv.classList.toggle('hidden', this.signature == '');
      return DOMPurify.sanitize(buffer.innerHTML);
    },
    updateFooter(content){
      const buffer = document.createElement('div');
      buffer.innerHTML = DOMPurify.sanitize(content);
      buffer.querySelector(`#${this.footerId}`).src = this.footerData;
      return DOMPurify.sanitize(buffer.innerHTML);
    },
    customID(){
      // return a string with 8 characters A-Z
      return new Array(8)
          .fill(0)
          .map( () => String.fromCharCode(65 + Math.floor( Math.random() * 25 )) )
          .join('');
    },
  },
  computed: {
    ...mapGetters("email", [
      "firstSelectedEmail",
      "sendState",
    ]),
    content: {
      get(){
        return this.value;
      },
      set(content){
        this.$emit('input', content);
      }
    },
    footerData(){
      return (this.footer?.mimeType && this.footer?.contents) ? `data:${this.footer.mimeType};base64,${this.footer.contents}` : '';
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/_default.scss";
.email-info{
  min-height: 115px;
  height: auto;
}
iframe{
  border: none;
}
a{
  text-decoration: underline;
  text-decoration-color: $color-black;
}
.send-button-container{
  padding-top: 2.5px;
  padding-bottom: 2.5px;
}
</style>

<style lang="scss">
@import "~@/_default.scss";
.fr-box.fr-basic.fr-top{
  flex: 1;
  display: flex;
  flex-direction: column;
}
.fr-wrapper{
  flex: 1 !important;
}
.fr-second-toolbar{
  border: none !important;
}
</style>
